import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { useStores } from 'stores/hooks/hooks';
import { Paper } from '@mui/material';
import {
  HALF_SCREEN_TABLE_TOP_HEIGHT,
  HIGHLIGHTED_ROW_CLASS_NAME,
} from 'core/constants';
import { FilterMatchMode } from 'primereact/api';
import { DataTableSelectEvent } from 'primereact/datatable';

const COLUMN_STYLE = { minWidth: '100px' };
const COLUMN_STYLE_CHECK = { minWidth: '3rem' };

// This is a work-around fix for prime-react v8 issue with the DataTable component
// Tables with the 'select all' checkbox have an issue with double events being fired (does not prevent propagation)
const StyledDataTable = styled(DataTable)`
  &&& {
    .p-checkbox {
      justify-self: center;
      display: flex;
      justify-items: center;
    }
  }
`;

const DataTableWrapper = styled(Paper)`
  /* max-height: ${HALF_SCREEN_TABLE_TOP_HEIGHT}; */

  background-color: transparent;
  margin: 1rem;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .${HIGHLIGHTED_ROW_CLASS_NAME} {
    td {
      background-color: var(--highlight-bg);
    }
  }
`;

const StyledBlockContainer = styled(Grid)`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const ActionsTable: FC<{ isLoading: boolean }> = observer(({ isLoading }) => {
  const { t } = useTranslation('grower');
  const { actionsStore } = useStores();
  const { actionsCall, systemsCall } = actionsStore;

  const formattedActions = useMemo(
    () =>
      actionsCall.data.map((action) => ({
        ...action,
        system: systemsCall.data.find((system) => system.id === action.systemId)
          ?.displayName,
      })),
    [actionsCall.data, systemsCall.data],
  );

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    name: { value: '', matchMode: FilterMatchMode.CONTAINS },
    serial: { value: '', matchMode: FilterMatchMode.CONTAINS },
    mode: { value: '', matchMode: FilterMatchMode.CONTAINS },
    value: { value: '', matchMode: FilterMatchMode.CONTAINS },
    system: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(
    ({ data }: DataTableSelectEvent) => {
      const index = actionsStore.selectedActions.findIndex(
        (plot) => plot.id === data.id,
      );

      if (index !== -1) {
        actionsStore.setSelectedActions([]);
      } else {
        actionsStore.setSelectedActions([data]);
      }
    },
    [actionsStore],
  );

  if (actionsCall.loading || isLoading) {
    return <Loading />;
  }

  return (
    <StyledBlockContainer>
      <DataTableWrapper elevation={3}>
        <StyledDataTable
          showGridlines
          resizableColumns
          scrollable
          width="100%"
          stripedRows
          dataKey="id"
          id="ResellerPlotsTable"
          filterDisplay="row"
          selectAll={false}
          value={formattedActions}
          selection={actionsStore.selectedActions}
          // onSelectAllChange={onSelectAllChange}
          onRowSelect={onRowClick}
          onRowUnselect={onRowClick}
          scrollHeight="flex"
          // rowClassName={getRowClassName}
          emptyMessage={t('empty_message')}
          filters={filters}
          // globalFilterFields={globalFilterFields}
          globalFilterFields={['name', 'serial', 'mode', 'value', 'system']}
        >
          <Column
            selectionMode="multiple"
            style={COLUMN_STYLE_CHECK}
            filterHeaderStyle={{ pointerEvents: 'none', opacity: 0 }}
          />
          <Column field="name" header={t('name')} sortable filter />
          <Column field="serial" header={t('serial')} sortable filter />
          <Column field="mode" header={t('mode')} sortable filter />
          <Column field="value" header={t('value')} sortable filter />
          <Column field="system" header={t('system')} sortable filter />
        </StyledDataTable>
      </DataTableWrapper>
    </StyledBlockContainer>
  );
});

export default ActionsTable;
